import React, { useMemo, useState } from 'react';

import { IconButton } from '@eika/button';
import { IconTypes } from '@eika/icon';
import { ToggleSwitch } from '@eika/toggle-switch';

import KortbetjeningModal from '../kortbetjening-modal/KortbetjeningModal';
import { AccessInfo, AgeGroup, CardControlLoader, CardRule, Piwik, SiteCoreDictionary } from '../types';

type FormDisabledInfoProps = {
    accessInfo: AccessInfo;
    piwik: Piwik | undefined;
    dictionary: SiteCoreDictionary;
};

const FormDisabledInfo = ({ accessInfo, piwik, dictionary }: FormDisabledInfoProps) => {
    const { userAgeGroup, userIsHighFraudRisk } = accessInfo;
    const { regionEcommerceInfoTeenager, regionInfoUserIsHighFraudRisk, regionEcommerceChangeInfoChild } = dictionary;
    if (userAgeGroup === AgeGroup.CHILD) {
        return (
            <p
                data-track-id={piwik?.regionBlockEcommerce?.ecommerceChildDisabledInfo?.dataTrackId}
                data-track-linkname={piwik?.regionBlockEcommerce?.ecommerceChildDisabledInfo?.dataTrackLinkname}
                id={piwik?.regionBlockEcommerce?.ecommerceChildDisabledInfo?.dataTrackId}
            >
                {regionEcommerceChangeInfoChild}
            </p>
        );
    }

    if (userAgeGroup === AgeGroup.TEENAGER) {
        return (
            <p
                data-track-id={piwik?.regionBlockEcommerce?.ecommerceTeenagerDisabledInfo?.dataTrackId}
                data-track-linkname={piwik?.regionBlockEcommerce?.ecommerceTeenagerDisabledInfo?.dataTrackLinkname}
                id={piwik?.regionBlockEcommerce?.ecommerceTeenagerDisabledInfo?.dataTrackId}
            >
                {regionEcommerceInfoTeenager}
            </p>
        );
    }

    if (userIsHighFraudRisk) {
        return (
            <p
                data-track-id={piwik?.regionBlockEcommerce?.ecommerceUserIsHighFraudRiskDisabledInfo?.dataTrackId}
                data-track-linkname={
                    piwik?.regionBlockEcommerce?.ecommerceUserIsHighFraudRiskDisabledInfo?.dataTrackLinkname
                }
                id={piwik?.regionBlockEcommerce?.ecommerceUserIsHighFraudRiskDisabledInfo?.dataTrackId}
            >
                {regionInfoUserIsHighFraudRisk}
            </p>
        );
    }

    return null;
};

interface EcommerceProps {
    cardControlLoader: CardControlLoader;
    cardRule: CardRule;
    handleEcommerceChange: (enabled: boolean) => void;
    accessInfo: AccessInfo;
    dictionary: SiteCoreDictionary;
    piwik: Piwik | undefined;
}

export default ({
    cardControlLoader,
    cardRule,
    handleEcommerceChange,
    accessInfo,
    dictionary,
    piwik,
}: EcommerceProps): JSX.Element | null => {
    const [showModal, setShowModal] = useState<boolean>(false);
    const { regionEcommerceInfoChild, regionLabelAllowEcommerce, regionEcommerce, ecommerceText } = dictionary;

    const { userAgeGroup, userIsHighFraudRisk } = accessInfo;
    const { isPostingCardControl } = cardControlLoader;

    const ecommerceInfo = accessInfo.infoToIncludeEcommerce;

    if (typeof ecommerceInfo === 'undefined') {
        return null;
    }

    const formDisabled = useMemo(
        () => userAgeGroup !== AgeGroup.ADULT || isPostingCardControl || userIsHighFraudRisk,
        [userAgeGroup, userIsHighFraudRisk, isPostingCardControl],
    );

    return (
        <>
            <section
                className="block-category"
                data-track-id={piwik?.regionBlockEcommerce?.ecommerceForm?.dataTrackId}
                data-track-linkname={piwik?.regionBlockEcommerce?.ecommerceForm?.dataTrackLinkname}
                id={piwik?.regionBlockEcommerce?.ecommerceForm?.dataTrackId}
            >
                <div className="region-info-header">
                    <h3>{regionEcommerce}</h3>
                    <IconButton
                        screenReaderText={regionEcommerce}
                        data-track-id={piwik?.regionBlockEcommerce?.ecommerceInfoButton?.dataTrackId}
                        data-track-linkname={piwik?.regionBlockEcommerce?.ecommerceInfoButton?.dataTrackLinkname}
                        icon={IconTypes.INFO_24}
                        id={piwik?.regionBlockEcommerce?.ecommerceInfoButton?.dataTrackId}
                        onClick={() => setShowModal(true)}
                    />
                </div>
                {ecommerceInfo.cardOwnerAgeGroup === AgeGroup.CHILD && (
                    <p
                        data-track-id={piwik?.regionBlockEcommerce?.ecommerceChildInfo?.dataTrackId}
                        data-track-linkname={piwik?.regionBlockEcommerce?.ecommerceChildInfo?.dataTrackLinkname}
                        id={piwik?.regionBlockEcommerce?.ecommerceChildInfo?.dataTrackId}
                    >
                        {regionEcommerceInfoChild}
                    </p>
                )}
                {ecommerceInfo.cardOwnerAgeGroup !== AgeGroup.CHILD && (
                    <>
                        <FormDisabledInfo accessInfo={accessInfo} piwik={piwik} dictionary={dictionary} />
                        <div
                            className="toggleContainer ecommerce"
                            data-track-id={piwik?.regionBlockEcommerce?.ecommerceToggle?.dataTrackId}
                            data-track-linkname={piwik?.regionBlockEcommerce?.ecommerceToggle?.dataTrackLinkname}
                            id={piwik?.regionBlockEcommerce?.ecommerceToggle?.dataTrackId}
                        >
                            <div className="toggleRow">
                                <p>{regionLabelAllowEcommerce}</p>
                                <ToggleSwitch
                                    ariaLabel={regionLabelAllowEcommerce}
                                    checked={cardRule.ecommerce}
                                    data-track-id={piwik?.regionBlockEcommerce?.ecommerceToggleSwitch?.dataTrackId}
                                    data-track-linkname={
                                        piwik?.regionBlockEcommerce?.ecommerceToggleSwitch?.dataTrackLinkname
                                    }
                                    disabled={formDisabled}
                                    onChange={(enabled: boolean) => {
                                        handleEcommerceChange(enabled);
                                    }}
                                />
                            </div>
                        </div>
                    </>
                )}
            </section>

            <KortbetjeningModal
                id="card-action"
                appNamespace="block-region-info"
                open={showModal}
                setOpen={() => setShowModal(false)}
                shouldCloseOnOverlayClick
                data-track-id={piwik?.regionBlockEcommerce?.ecommerceInfo?.dataTrackId}
                data-track-linkname={piwik?.regionBlockEcommerce?.ecommerceInfo?.dataTrackLinkname}
            >
                <article className="information-wrapper">
                    <h1>{`Informasjon om ${regionEcommerce.toLowerCase()}`}</h1>
                    <p>{ecommerceText}</p>
                </article>
            </KortbetjeningModal>
        </>
    );
};
